import React, { useState, useEffect, useCallback } from 'react';
import { salaryAckApi, staffApi } from '../api/client';
import { Spin, Empty, Notice } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const fmtMoney = n => '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const MNF = ['January','February','March','April','May','June',
             'July','August','September','October','November','December'];

function monthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${MNF[parseInt(mo, 10) - 1]} ${y}`;
}

function SignatureModal({ ack, onClose }) {
  if (!ack) return null;
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:580 }}>
        <div className="m-hd">
          <span className="m-title">
            Signature — {ack.staff_name} — {monthLabel(ack.month)}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding:4 }}>✕</button>
        </div>
        <div className="m-body">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14, fontSize:13 }}>
            <div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:2 }}>Staff</div>
              <div style={{ fontWeight:600 }}>{ack.staff_name}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:2 }}>Month</div>
              <div style={{ fontWeight:600 }}>{monthLabel(ack.month)}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:2 }}>Amount Acknowledged</div>
              <div style={{ fontWeight:700, fontSize:15, color:'var(--green)' }}>
                {fmtMoney(ack.amount_received)}
              </div>
            </div>
            <div>
              <div style={{ fontSize:11, color:'var(--text3)', marginBottom:2 }}>Submitted On</div>
              <div>
                {new Date(ack.acknowledged_at).toLocaleDateString('en-IN', { dateStyle:'long' })}{' '}
                <span style={{ color:'var(--text3)', fontSize:12 }}>
                  {new Date(ack.acknowledged_at).toLocaleTimeString('en-IN', { timeStyle:'short', hour12:true })}
                </span>
              </div>
            </div>
            {ack.notes && (
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:11, color:'var(--text3)', marginBottom:2 }}>Notes</div>
                <div style={{ fontSize:13 }}>{ack.notes}</div>
              </div>
            )}
          </div>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6, fontWeight:600 }}>
            SIGNATURE
          </div>
          <img
            src={ack.signature_data}
            alt="Signature"
            style={{
              width:'100%', border:'1px solid var(--border)',
              borderRadius:6, background:'#fafbfc',
            }}
          />
        </div>
        <div className="m-ft">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function SalaryAcknowledgements() {
  const { isSA } = useAuth();
  const now = new Date();
  const [acks, setAcks]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [busy, setBusy]         = useState(true);
  const [filterStaff, setFS]    = useState('');
  const [filterMonth, setFM]    = useState('');
  const [viewAck, setViewAck]   = useState(null);

  if (!isSA) return <Navigate to="/dashboard" replace/>;

  // Generate last 18 months for filter
  const monthOptions = [];
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`;
    monthOptions.push({ val, lbl: monthLabel(val) });
  }

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filterStaff) params.staff_id = filterStaff;
      if (filterMonth) params.month    = filterMonth;
      const { data } = await salaryAckApi.list(params);
      setAcks(data);
    } catch {
      // silently ignore — the isSA guard below handles redirect for unauthorised users
    } finally {
      setBusy(false);
    }
  }, [filterStaff, filterMonth]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    staffApi.list().then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  // Group by staff for summary
  const summary = {};
  acks.forEach(a => {
    if (!summary[a.staff_id]) summary[a.staff_id] = { name: a.staff_name, count: 0, total: 0 };
    summary[a.staff_id].count++;
    summary[a.staff_id].total += Number(a.amount_received);
  });

  const sel = {
    height:32, border:'1px solid var(--border)', borderRadius:6,
    fontSize:12, padding:'0 8px', background:'var(--surface)',
  };

  return (
    <div>
      <SignatureModal ack={viewAck} onClose={() => setViewAck(null)}/>

      <div className="page-hd">
        <h2>Salary Acknowledgements</h2>
      </div>

      <Notice type="info">
        This page is only visible to super admin. It shows salary acknowledgements
        submitted by field staff as proof of receiving their salary.
      </Notice>

      {/* Summary cards */}
      {!filterStaff && !filterMonth && Object.keys(summary).length > 0 && (
        <div style={{ margin:'16px 0' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Staff Summary</div>
          <div className="stat-grid" style={{ gridTemplateColumns:'repeat(auto-fill, minmax(180px,1fr))' }}>
            {Object.values(summary).map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-lbl">{s.name}</div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>
                  {s.count} submission{s.count !== 1 ? 's' : ''}
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--green)', marginTop:4 }}>
                  {fmtMoney(s.total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
        <select value={filterStaff} onChange={e => setFS(e.target.value)} style={sel}>
          <option value="">All Staff</option>
          {staff.filter(s => s.is_active).map(s => (
            <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
          ))}
        </select>
        <select value={filterMonth} onChange={e => setFM(e.target.value)} style={sel}>
          <option value="">All Months</option>
          {monthOptions.map(m => <option key={m.val} value={m.val}>{m.lbl}</option>)}
        </select>
        {(filterStaff || filterMonth) && (
          <button className="btn btn-sm" style={{ height:32, fontSize:12 }}
            onClick={() => { setFS(''); setFM(''); }}>
            Clear
          </button>
        )}
        {busy && <Spin/>}
      </div>

      {/* Table */}
      <div className="card card-0">
        {acks.length === 0
          ? <Empty msg="No salary acknowledgements found"/>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Month</th>
                    <th>Amount Acknowledged</th>
                    <th>Submitted On</th>
                    <th>Notes</th>
                    <th>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {acks.map(a => (
                    <tr key={a.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <span style={{
                            width:28, height:28, borderRadius:'50%',
                            background:'var(--navy)', color:'#fff',
                            fontSize:11, fontWeight:700,
                            display:'inline-flex', alignItems:'center', justifyContent:'center',
                            flexShrink:0,
                          }}>
                            {(a.staff_name || '?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}
                          </span>
                          <span style={{ fontWeight:600, fontSize:13 }}>{a.staff_name || '—'}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight:600 }}>{monthLabel(a.month)}</td>
                      <td>
                        <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:14, color:'var(--green)' }}>
                          {fmtMoney(a.amount_received)}
                        </span>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>
                        {new Date(a.acknowledged_at).toLocaleDateString('en-IN', { dateStyle:'medium' })}
                        <br/>
                        <span style={{ color:'var(--text3)', fontSize:11 }}>
                          {new Date(a.acknowledged_at).toLocaleTimeString('en-IN', { timeStyle:'short', hour12:true })}
                        </span>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text3)', maxWidth:180 }}>{a.notes || '—'}</td>
                      <td>
                        <button
                          className="btn btn-sm"
                          onClick={() => setViewAck(a)}
                          style={{ fontSize:11 }}
                        >
                          View Signature
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  );
}
